import { Button } from "@/components/ui/button";
import { cn } from "@/utils";
import { Loader2 } from "lucide-react";
import { ReactNode } from "react";
import { Link } from "react-router-dom";

export const publicAuthInputClassName =
  "h-11 border-white/10 bg-white/5 pl-10 text-white placeholder:text-slate-500 focus-visible:border-emerald-500/50 focus-visible:ring-emerald-500/20";

export const publicAuthLabelClassName = "text-slate-300 font-medium";

type PublicAuthCardProps = {
  title: string;
  description: string;
  children: ReactNode;
  footer?: ReactNode;
};

export function PublicAuthCard({
  title,
  description,
  children,
  footer,
}: PublicAuthCardProps) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-8 shadow-2xl shadow-black/20 backdrop-blur-xl">
      <div className="mb-8 space-y-2">
        <h2 className="text-2xl font-bold tracking-tight text-white">{title}</h2>
        <p className="text-sm text-slate-400">{description}</p>
      </div>

      {children}

      {footer}
    </div>
  );
}

type PublicAuthSubmitButtonProps = {
  isSubmitting: boolean;
  loadingText: string;
  children: ReactNode;
  className?: string;
};

export function PublicAuthSubmitButton({
  isSubmitting,
  loadingText,
  children,
  className,
}: PublicAuthSubmitButtonProps) {
  return (
    <Button
      type="submit"
      disabled={isSubmitting}
      className={cn(
        "mt-2 h-11 w-full rounded-lg bg-emerald-500 text-sm font-semibold text-white shadow-lg shadow-emerald-500/25 transition-all hover:bg-emerald-400 hover:shadow-emerald-400/30 disabled:opacity-70",
        className
      )}
    >
      {isSubmitting ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          {loadingText}
        </>
      ) : (
        children
      )}
    </Button>
  );
}

type PublicAuthFooterLinkProps = {
  text: string;
  linkText: string;
  to: string;
};

export function PublicAuthFooterLink({
  text,
  linkText,
  to,
}: PublicAuthFooterLinkProps) {
  return (
    <p className="mt-6 text-center text-sm text-slate-400">
      {text}{" "}
      <Link
        to={to}
        className="font-medium text-emerald-400 transition-colors hover:text-emerald-300"
      >
        {linkText}
      </Link>
    </p>
  );
}
